import { useEffect, useRef, useState } from "react";
import { Navigate, useSearchParams, Link } from "react-router-dom";
import {
  TrendingUp,
  GitBranch,
  Users,
  Building2,
  Crown,
  UserPlus,
  UserCheck2,
  CheckCircle2,
  Plus,
  Filter,
  Trash2,
  UserCheck,
  UserX,
  Calendar,
  BookOpen,
  FileText,
  CreditCard,
  Mail,
  X,
  Upload,
  Image as ImageIcon,
} from "lucide-react";
import DashboardShell, { ColorStat } from "@/components/DashboardShell";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "overview";

  const setTab = (t) =>
    setSp(t === "overview" ? {} : { tab: t });

  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [pending, setPending] = useState([]);
  const [payments, setPayments] = useState([]);
  const [events, setEvents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [posts, setPosts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "admin") return;

    api
      .get("/admin/stats")
      .then((r) => setStats(r.data))
      .catch(() => {});

    api
      .get("/admin/members")
      .then((r) => setMembers(r.data))
      .catch(() => {});

    api
      .get("/admin/pending")
      .then((r) => setPending(r.data))
      .catch(() => {});

    api
      .get("/admin/payments")
      .then((r) => setPayments(r.data))
      .catch(() => {});

    api
      .get("/events")
      .then((r) => setEvents(r.data))
      .catch(() => {});

    api
      .get("/courses")
      .then((r) => setCourses(r.data))
      .catch(() => {});

    api
      .get("/blog")
      .then((r) => setPosts(r.data))
      .catch(() => {});

    api
      .get("/admin/contact-messages")
      .then((r) => setContacts(r.data))
      .catch(() => {});

    api
      .get("/chapters")
      .then((r) => setChapters(r.data))
      .catch(() => {});
  }, [user]);

  if (loading) {
    return (
      <div className="container-luxe py-32 text-center text-plumSoft">
        Loading…
      </div>
    );
  }

  if (!user || user.role !== "admin") {
    return <Navigate to="/login" replace />;
  }

  const approve = async (id) => {
    try {
      await api.post(`/admin/members/${id}/approve`);

      setPending((current) =>
        current.filter((p) => p.id !== id)
      );

      toast.success("Approved");
    } catch {
      toast.error("Failed");
    }
  };

  const reject = async (id) => {
    const r = prompt("Reason (optional):") || "";

    try {
      await api.post(`/admin/members/${id}/reject`, {
        reason: r,
      });

      setPending((current) =>
        current.filter((p) => p.id !== id)
      );

      toast.success("Rejected");
    } catch {
      toast.error("Failed");
    }
  };

  const deleteM = async (id) => {
    if (!confirm("Delete permanently?")) return;

    try {
      await api.delete(`/admin/members/${id}`);

      setMembers((current) =>
        current.filter((m) => m.id !== id)
      );

      toast.success("Deleted");
    } catch {
      toast.error("Failed");
    }
  };

  const revenue = stats ? stats.revenue_inr : 0;

  const revFmt =
    revenue >= 100000
      ? `₹${(revenue / 100000).toFixed(1)}L`
      : `₹${revenue.toLocaleString("en-IN")}`;

  const actions = (
    <>
      <button className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full border border-aubergine/15 text-xs uppercase tracking-widest text-plum bg-white hover:bg-aubergine/5">
        <Filter className="w-3.5 h-3.5" />
        All Time
      </button>

      <button
        onClick={() => setTab("pending")}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-aubergine text-ivory text-xs uppercase tracking-widest hover:bg-aubergineLight"
      >
        <UserCheck2 className="w-3.5 h-3.5" />
        Approvals ({pending.length})
      </button>
    </>
  );

  return (
    <DashboardShell
      role="admin"
      title={`Welcome, ${user.name?.split(" ")[0] || "Admin"}`}
      subtitle="Super Admin · WOWSHE Control Room"
      actions={actions}
    >
      {/* =========================
          OVERVIEW
      ========================== */}
      {tab === "overview" && stats && (
        <div
          data-testid="admin-overview"
          className="space-y-7"
        >
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <ColorStat
              label="Total Revenue"
              value={revFmt}
              tone="orange"
              icon={TrendingUp}
            />

            <ColorStat
              label="Total Referrals"
              value={stats.referrals}
              tone="teal"
              icon={GitBranch}
            />

            <ColorStat
              label="Total Members"
              value={stats.members}
              tone="purple"
              icon={Users}
            />

            <ColorStat
              label="Active Chapters"
              value={
                chapters.filter(
                  (c) =>
                    !(c.founded || "").startsWith("Coming")
                ).length || 1
              }
              tone="blue"
              icon={Building2}
            />

            <ColorStat
              label="Pending Approvals"
              value={pending.length}
              tone="rose"
              icon={Crown}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-purple-50 rounded-2xl p-7">
              <div className="flex items-start justify-between mb-4">
                <p className="text-sm text-plumSoft">
                  Annual Members
                </p>

                <div className="w-11 h-11 rounded-xl bg-purple-400 text-white flex items-center justify-center">
                  <UserPlus className="w-5 h-5" />
                </div>
              </div>

              <p className="font-display text-5xl text-aubergine">
                {stats.annual_members}
              </p>

              <p className="text-xs text-plumSoft mt-2">
                of {stats.members} total ·{" "}
                {Math.round(
                  (stats.annual_members /
                    Math.max(stats.members, 1)) *
                    100
                )}
                % activation
              </p>
            </div>

            <div className="bg-emerald-50 rounded-2xl p-7">
              <div className="flex items-start justify-between mb-4">
                <p className="text-sm text-plumSoft">
                  Paid Orders
                </p>

                <div className="w-11 h-11 rounded-xl bg-emerald-400 text-white flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
              </div>

              <p className="font-display text-5xl text-aubergine">
                {stats.paid_orders}
              </p>

              <p className="text-xs text-plumSoft mt-2">
                {stats.events} events · {stats.courses} courses ·{" "}
                {stats.blog_posts} posts
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-aubergine/8 p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl text-aubergine flex items-center gap-2">
                <Crown className="w-4 h-4 text-champagne" />
                Chapter Performance
              </h3>

              <Link
                to="/chapters"
                className="text-xs text-plum link-underline uppercase tracking-widest"
              >
                View all
              </Link>
            </div>

            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] tracking-widest uppercase text-plum">
                  <th className="text-left pb-3 font-medium">
                    Chapter
                  </th>
                  <th className="text-left pb-3 font-medium">
                    City
                  </th>
                  <th className="text-left pb-3 font-medium">
                    Status
                  </th>
                  <th className="text-right pb-3 font-medium">
                    Members
                  </th>
                </tr>
              </thead>

              <tbody>
                {chapters.map((c) => (
                  <tr
                    key={c.id}
                    className="border-t border-aubergine/5"
                  >
                    <td className="py-3">
                      <Link
                        to={`/chapters/${c.id}`}
                        className="text-aubergine font-medium link-underline"
                      >
                        {c.name}
                      </Link>
                    </td>

                    <td className="py-3 text-plumSoft">
                      {c.city}
                    </td>

                    <td className="py-3">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] uppercase ${
                          (c.founded || "").startsWith(
                            "Coming"
                          )
                            ? "bg-lavender text-plum"
                            : "bg-emerald-50 text-emerald-600"
                        }`}
                      >
                        {(c.founded || "").startsWith(
                          "Coming"
                        )
                          ? "Soon"
                          : "Live"}
                      </span>
                    </td>

                    <td className="py-3 text-right text-aubergine font-medium">
                      {c.members_count || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* =========================
          PENDING
      ========================== */}
      {tab === "pending" && (
        <div
          data-testid="admin-pending"
          className="space-y-3"
        >
          {pending.length === 0 ? (
            <div className="bg-white rounded-2xl border border-aubergine/8 p-12 text-center">
              <UserCheck className="w-10 h-10 mx-auto text-emerald-500" />

              <p className="font-display text-xl text-aubergine mt-4">
                No pending applications
              </p>

              <p className="text-sm text-plumSoft font-light mt-2">
                All caught up.
              </p>
            </div>
          ) : (
            pending.map((p) => (
              <div
                key={p.id}
                data-testid={`pending-row-${p.id}`}
                className="bg-white rounded-2xl border border-aubergine/8 p-5 flex flex-wrap items-center justify-between gap-4 hover:shadow-luxe transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-rose to-plum text-ivory flex items-center justify-center font-display text-xl overflow-hidden">
                    {p.avatar ? (
                      <img
                        src={p.avatar}
                        alt={p.name || "Member"}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      p.name?.[0] || "?"
                    )}
                  </div>

                  <div>
                    <p className="font-display text-lg text-aubergine">
                      {p.name}
                    </p>

                    <p className="text-xs text-plumSoft mt-0.5">
                      {p.email} · {p.phone || "—"}
                    </p>

                    <p className="text-xs text-plum mt-1">
                      {p.city || "—"}
                      {p.industry && ` · ${p.industry}`}
                      {p.business && ` · ${p.business}`}
                    </p>

                    <p className="text-[10px] text-plumSoft mt-1">
                      Applied{" "}
                      {p.applied_at
                        ? new Date(
                            p.applied_at
                          ).toLocaleDateString("en-IN")
                        : "—"}
                    </p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    data-testid={`approve-${p.id}`}
                    onClick={() => approve(p.id)}
                    className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-4 py-2 text-xs uppercase tracking-widest"
                  >
                    <UserCheck className="w-3.5 h-3.5" />
                    Approve
                  </button>

                  <button
                    data-testid={`reject-${p.id}`}
                    onClick={() => reject(p.id)}
                    className="inline-flex items-center gap-1.5 border border-aubergine/15 text-plum rounded-full px-4 py-2 text-xs uppercase tracking-widest hover:bg-rose/5"
                  >
                    <UserX className="w-3.5 h-3.5" />
                    Reject
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* =========================
          MEMBERS
      ========================== */}
      {tab === "members" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-plumSoft text-sm">
              {members.length} member
              {members.length !== 1 && "s"}
            </p>

            <button
              data-testid="admin-add-member"
              onClick={() => setAddOpen(true)}
              className="inline-flex items-center gap-2 bg-aubergine text-ivory px-5 py-2.5 rounded-full text-[12px] uppercase tracking-widest hover:bg-aubergine/90"
            >
              <UserPlus className="w-4 h-4" />
              Add Member
            </button>
          </div>

          <div className="bg-white rounded-2xl border border-aubergine/8 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-lavender/40">
                <tr>
                  {[
                    "Name",
                    "Email",
                    "Chapter",
                    "Status",
                    "Joined",
                    "",
                  ].map((c) => (
                    <th
                      key={c}
                      className="p-4 text-left text-[10px] tracking-widest uppercase text-plum font-medium"
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {members.map((m) => (
                  <tr
                    key={m.id}
                    className="border-t border-aubergine/5"
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full overflow-hidden bg-lavender flex items-center justify-center shrink-0">
                          {m.avatar ? (
                            <img
                              src={m.avatar}
                              alt={m.name || "Member"}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="font-display text-sm text-aubergine">
                              {m.name?.[0] || "?"}
                            </span>
                          )}
                        </div>

                        <Link
                          to={`/members/${m.id}`}
                          className="text-aubergine link-underline font-medium"
                        >
                          {m.name}
                        </Link>
                      </div>
                    </td>

                    <td className="p-4 text-plumSoft text-xs">
                      {m.email}
                    </td>

                    <td className="p-4 text-xs">
                      {m.chapter || "—"}
                    </td>

                    <td className="p-4">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] uppercase ${
                          m.membership_status === "annual"
                            ? "bg-amber-50 text-amber-600"
                            : "bg-lavender text-plum"
                        }`}
                      >
                        {m.membership_status}
                      </span>
                    </td>

                    <td className="p-4 text-xs text-plumSoft">
                      {m.joined_at
                        ? new Date(
                            m.joined_at
                          ).toLocaleDateString("en-IN")
                        : "—"}
                    </td>

                    <td className="p-4">
                      {m.role !== "admin" && (
                        <button
                          onClick={() => deleteM(m.id)}
                          className="text-rose hover:text-aubergine"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* =========================
          PAYMENTS
      ========================== */}
      {tab === "payments" && (
        <div className="bg-white rounded-2xl border border-aubergine/8 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-lavender/40">
              <tr>
                {[
                  "Order ID",
                  "User",
                  "Kind",
                  "Amount",
                  "Status",
                  "Date",
                ].map((c) => (
                  <th
                    key={c}
                    className="p-4 text-left text-[10px] tracking-widest uppercase text-plum font-medium"
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {payments.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="p-10 text-center text-plumSoft"
                  >
                    No payments yet.
                  </td>
                </tr>
              )}

              {payments.map((p) => (
                <tr
                  key={p.id}
                  className="border-t border-aubergine/5 text-xs"
                >
                  <td className="p-4 font-mono">
                    {p.razorpay_order_id?.slice(-10)}
                  </td>

                  <td className="p-4 text-plumSoft">
                    {p.user_id?.slice(-6)}
                  </td>

                  <td className="p-4">{p.kind}</td>

                  <td className="p-4 text-aubergine">
                    ₹{" "}
                    {(
                      p.amount_paise / 100
                    ).toLocaleString("en-IN")}
                  </td>

                  <td className="p-4">
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] uppercase ${
                        p.status === "paid"
                          ? "bg-emerald-50 text-emerald-600"
                          : "bg-lavender text-plum"
                      }`}
                    >
                      {p.status}
                    </span>
                  </td>

                  <td className="p-4 text-plumSoft">
                    {new Date(
                      p.created_at
                    ).toLocaleDateString("en-IN")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* =========================
          EVENTS / COURSES / BLOG
      ========================== */}
      {["events", "courses", "blog"].includes(tab) && (
        <div className="space-y-3">
          {(tab === "events"
            ? events
            : tab === "courses"
            ? courses
            : posts
          ).map((x, i) => (
            <div
              key={x.id || i}
              className="bg-white rounded-2xl border border-aubergine/8 p-5 flex items-center justify-between gap-4"
            >
              <div className="flex items-center gap-4">
                <div
                  className={`w-11 h-11 rounded-xl flex items-center justify-center text-white ${
                    tab === "events"
                      ? "bg-sky-400"
                      : tab === "courses"
                      ? "bg-purple-400"
                      : "bg-orange-400"
                  }`}
                >
                  {tab === "events" ? (
                    <Calendar className="w-5 h-5" />
                  ) : tab === "courses" ? (
                    <BookOpen className="w-5 h-5" />
                  ) : (
                    <FileText className="w-5 h-5" />
                  )}
                </div>

                <div>
                  <p className="font-display text-lg text-aubergine">
                    {x.title}
                  </p>

                  <p className="text-xs text-plumSoft mt-0.5">
                    {tab === "events"
                      ? `${x.type} · ${new Date(
                          x.date
                        ).toLocaleDateString("en-IN")} · ${
                          x.location
                        }`
                      : tab === "courses"
                      ? `${x.category} · ${x.duration} · ${x.instructor}`
                      : `${x.category} · by ${x.author}`}
                  </p>
                </div>
              </div>

              <p className="text-sm text-plum">
                {tab === "courses"
                  ? `₹ ${x.price?.toLocaleString("en-IN")}`
                  : tab === "events"
                  ? `${(x.attendees || []).length} attending`
                  : new Date(
                      x.created_at
                    ).toLocaleDateString("en-IN")}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* =========================
          MESSAGES
      ========================== */}
      {tab === "messages" && (
        <div className="space-y-3">
          {contacts.length === 0 && (
            <p className="text-plumSoft text-center py-10">
              No messages yet.
            </p>
          )}

          {contacts.map((c) => (
            <div
              key={c.id}
              className="bg-white rounded-2xl border border-aubergine/8 p-5"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-rose/15 text-rose flex items-center justify-center">
                  <Mail className="w-4 h-4" />
                </div>

                <div className="flex-1">
                  <p className="font-display text-lg text-aubergine">
                    {c.subject}
                  </p>

                  <p className="text-xs text-plum">
                    {c.name} · {c.email}
                  </p>
                </div>

                <p className="text-xs text-plumSoft">
                  {new Date(
                    c.created_at
                  ).toLocaleDateString("en-IN")}
                </p>
              </div>

              <p className="text-sm text-plumSoft mt-3 font-light leading-relaxed pl-13">
                {c.message}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* =========================
          ADD MEMBER MODAL
      ========================== */}
      {addOpen && (
        <AddMemberModal
          onClose={() => setAddOpen(false)}
          onCreated={(newMember) => {
            setMembers((current) => [
              newMember,
              ...current,
            ]);
            setAddOpen(false);
          }}
        />
      )}
    </DashboardShell>
  );
}


/* ============================================================
   ADD MEMBER MODAL
============================================================ */

function AddMemberModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    city: "",
    industry: "",
    business: "",
    avatar: "",
  });

  const [imagePreview, setImagePreview] = useState("");
  const [busy, setBusy] = useState(false);

  const fileInputRef = useRef(null);

  /* ----------------------------------------------------------
     PASSWORD GENERATOR
  ---------------------------------------------------------- */

  const gen = () => {
    const chars =
      "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";

    let p = "";

    for (let i = 0; i < 10; i++) {
      p += chars[
        Math.floor(Math.random() * chars.length)
      ];
    }

    setForm((f) => ({
      ...f,
      password: p,
    }));
  };

  /* ----------------------------------------------------------
     PROFILE IMAGE
  ---------------------------------------------------------- */

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/webp",
      "image/gif",
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error(
        "Please upload a JPG, PNG, WEBP or GIF image."
      );

      e.target.value = "";
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error(
        "Profile photo must be 2 MB or smaller."
      );

      e.target.value = "";
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const imageData = reader.result;

      setForm((current) => ({
        ...current,
        avatar: imageData,
      }));

      setImagePreview(imageData);
    };

    reader.onerror = () => {
      toast.error("Unable to read the image.");
    };

    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setForm((current) => ({
      ...current,
      avatar: "",
    }));

    setImagePreview("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /* ----------------------------------------------------------
     SUBMIT
  ---------------------------------------------------------- */

  const submit = async (e) => {
    e.preventDefault();

    if (
      !form.name.trim() ||
      !form.email.trim() ||
      !form.password
    ) {
      toast.error(
        "Name, email and password are required"
      );
      return;
    }

    setBusy(true);

    try {
      /*
       * avatar is included as a Base64 data URL.
       *
       * Example:
       * data:image/jpeg;base64,/9j/4AAQ...
       *
       * Your backend must save this field if you want
       * the image to persist permanently.
       */
      const payload = {
        name: form.name,
        email: form.email,
        password: form.password,
        phone: form.phone,
        city: form.city,
        industry: form.industry,
        business: form.business,
        avatar: form.avatar || "",
      };

      const { data } = await api.post(
        "/admin/members",
        payload
      );

      toast.success(
        `${data.user.name} added — send them their password securely`
      );

      onCreated(data.user);
    } catch (e) {
      toast.error(
        formatErr(
          e.response?.data?.detail
        ) || "Failed to create member"
      );
    } finally {
      setBusy(false);
    }
  };

  /* ----------------------------------------------------------
     MODAL UI
  ---------------------------------------------------------- */

  return (
    <div className="fixed inset-0 z-50 bg-aubergine/70 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto">
      <form
        onSubmit={submit}
        className="bg-ivory rounded-2xl w-full max-w-2xl border border-aubergine/15 shadow-2xl my-8"
      >
        {/* HEADER */}

        <div className="p-6 border-b border-aubergine/[0.08] flex items-center justify-between">
          <h2 className="font-display text-2xl text-aubergine">
            Add New Member
          </h2>

          <button
            type="button"
            onClick={onClose}
            className="text-plum hover:text-aubergine p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* BODY */}

        <div className="p-6 space-y-5">

          {/* PROFILE PHOTO */}

          <div>
            <label className="block text-[11px] uppercase tracking-[0.14em] text-plumSoft/70 mb-2">
              Profile Photo
            </label>

            <div className="flex items-center gap-4">

              {/* PREVIEW */}

              <div className="w-20 h-20 rounded-full overflow-hidden border border-aubergine/15 bg-lavender flex items-center justify-center shrink-0">

                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Profile preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <ImageIcon className="w-7 h-7 text-plumSoft/50" />
                )}

              </div>

              {/* BUTTONS */}

              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleImageChange}
                  className="hidden"
                />

                <button
                  type="button"
                  onClick={() =>
                    fileInputRef.current?.click()
                  }
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-aubergine/25 text-plum text-xs uppercase tracking-widest hover:bg-aubergine/[0.04]"
                >
                  <Upload className="w-3.5 h-3.5" />

                  {imagePreview
                    ? "Change Photo"
                    : "Upload Photo"}
                </button>

                {imagePreview && (
                  <button
                    type="button"
                    onClick={removeImage}
                    className="ml-3 text-xs text-rose hover:text-aubergine"
                  >
                    Remove
                  </button>
                )}

                <p className="text-[10px] text-plumSoft/60 mt-2">
                  JPG, PNG, WEBP or GIF · Max 2 MB
                </p>
              </div>
            </div>
          </div>

          {/* NAME + EMAIL */}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            <MemberField
              label="Full name *"
              testid="am-name"
              value={form.name}
              onChange={(v) =>
                setForm({
                  ...form,
                  name: v,
                })
              }
              placeholder="Priya Sharma"
            />

            <MemberField
              label="Email *"
              testid="am-email"
              type="email"
              value={form.email}
              onChange={(v) =>
                setForm({
                  ...form,
                  email: v,
                })
              }
              placeholder="priya@example.com"
            />

          </div>

          {/* PASSWORD */}

          <div>
            <label className="block text-[11px] uppercase tracking-[0.14em] text-plumSoft/70 mb-1.5">
              Password *
            </label>

            <div className="flex gap-2">
              <input
                data-testid="am-password"
                value={form.password}
                onChange={(e) =>
                  setForm({
                    ...form,
                    password: e.target.value,
                  })
                }
                className="input-luxe flex-1"
                placeholder="At least 6 characters"
              />

              <button
                type="button"
                onClick={gen}
                className="px-4 py-2 rounded-lg border border-aubergine/25 text-plum text-xs uppercase tracking-widest hover:bg-aubergine/[0.04]"
              >
                Generate
              </button>
            </div>

            <p className="text-[10px] text-plumSoft/60 mt-1">
              Share this with the member securely — they can
              change it after login.
            </p>
          </div>

          {/* PHONE / CITY / BUSINESS / INDUSTRY */}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            <MemberField
              label="Phone"
              testid="am-phone"
              value={form.phone}
              onChange={(v) =>
                setForm({
                  ...form,
                  phone: v,
                })
              }
              placeholder="+91 …"
            />

            <MemberField
              label="City"
              testid="am-city"
              value={form.city}
              onChange={(v) =>
                setForm({
                  ...form,
                  city: v,
                })
              }
              placeholder="Hyderabad"
            />

            <MemberField
              label="Business"
              testid="am-business"
              value={form.business}
              onChange={(v) =>
                setForm({
                  ...form,
                  business: v,
                })
              }
              placeholder="Aurora Tech"
            />

            <MemberField
              label="Industry"
              testid="am-industry"
              value={form.industry}
              onChange={(v) =>
                setForm({
                  ...form,
                  industry: v,
                })
              }
              placeholder="Technology"
            />

          </div>

          <p className="text-[11px] text-plumSoft/70 italic">
            Member will be added as approved and can log in
            immediately.
          </p>

        </div>

        {/* FOOTER */}

        <div className="p-6 border-t border-aubergine/[0.08] flex items-center justify-end gap-3">

          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-[12px] uppercase tracking-widest text-plum hover:text-aubergine"
          >
            Cancel
          </button>

          <button
            type="submit"
            data-testid="am-submit"
            disabled={busy}
            className="inline-flex items-center gap-2 bg-aubergine text-ivory px-6 py-2.5 rounded-full text-[12px] uppercase tracking-widest hover:bg-aubergine/90 disabled:opacity-50"
          >
            {busy ? "Adding…" : "Add Member"}
          </button>

        </div>

      </form>
    </div>
  );
}


/* ============================================================
   MEMBER FIELD
============================================================ */

function MemberField({
  label,
  testid,
  value,
  onChange,
  placeholder,
  type = "text",
}) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.14em] text-plumSoft/70 mb-1.5">
        {label}
      </label>

      <input
        type={type}
        data-testid={testid}
        value={value}
        onChange={(e) =>
          onChange(e.target.value)
        }
        placeholder={placeholder}
        className="input-luxe"
      />
    </div>
  );
}